import React, { useCallback, useEffect, useRef, useState } from 'react';
import UaePassErrorState from 'flow/components/uae-pass/UaePassErrorState';
import UaePassSuccessState from 'flow/components/uae-pass/UaePassSuccessState';
import {
    uaePassErrors,
    uaePassSignInResult,
} from 'flow/constants/uae-pass/uae-pass-constants';
import useCountdown from 'flow/hooks/useCountdown';
import { useUAEPass } from 'flow/hooks/useUAEPass';
import useUaePassSuccessRedirect from 'flow/hooks/useUaePassSuccessRedirect';
import { useHistory, useLocation } from 'react-router-dom';
import {
    mapSignInErrorToUaePassError,
    parseErrorParam,
} from 'flow/utils/uaepass-api-utils';
import Loading from 'flow/components/ui-components/Loading';
import { routes } from 'flow/types/routes';

const UaePassWebCallback = () => {
    const history = useHistory();
    const location = useLocation();
    const handleSuccessComplete = useUaePassSuccessRedirect();

    const [hasTimedOut, setHasTimedOut] = useState(false);

    const {
        handleUaePassTokens,
        finishSignInData,
        uaePassError,
        finishSignInError,
        signInError,
    } = useUAEPass();

    const handleTimeout = useCallback(() => {
        setHasTimedOut(true);
    }, []);

    useCountdown(30, handleTimeout);

    const hasProcessedRef = useRef(false);

    useEffect(() => {
        if (hasProcessedRef.current) return;
        hasProcessedRef.current = true;

        const params = new URLSearchParams(location.search);
        const sessionToken = params.get('token');
        const error = parseErrorParam(params.get('error'));

        if (sessionToken || error) {
            history.replace(location.pathname);
            handleUaePassTokens({ sessionToken, error });
        } else {
            handleUaePassTokens({ error: uaePassErrors.MISSING_CODE });
        }
    }, [handleUaePassTokens, location.search, history, location.pathname]);

    /* This route has no idle state to return to, so go back to the sign-in screen. */
    const handleBackToLogin = useCallback(() => {
        history.push(routes.signin);
    }, [history]);

    const resolveErrorCode = () => {
        if (uaePassError) return uaePassError;
        if (signInError) return mapSignInErrorToUaePassError(signInError);
        if (hasTimedOut) return uaePassErrors.TIMEOUT;
        return uaePassErrors.GENERIC;
    };

    const renderCurrentState = () => {
        if (finishSignInData?.signInStatus === uaePassSignInResult.OK) {
            return <UaePassSuccessState onComplete={handleSuccessComplete} />;
        } else if (
            finishSignInData?.signInStatus === uaePassSignInResult.ERROR ||
            uaePassError ||
            finishSignInError ||
            signInError ||
            hasTimedOut
        ) {
            return (
                <UaePassErrorState
                    errorCode={resolveErrorCode()}
                    onBackToLogin={handleBackToLogin}
                />
            );
        }

        return <Loading disableTimeout={true} />;
    };

    return (
        <div className="dark:darken-25 flex h-full items-center bg-white">
            <div className="mx-auto flex w-full max-w-md flex-col">
                {renderCurrentState()}
            </div>
        </div>
    );
};

export default UaePassWebCallback;
